import { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { LocationReducerActionType, LocationData } from "../reducers/locationReducer";
import { DeviceContext } from "../contexts/DeviceContext";
import { LocationContext } from "../contexts/LocationContext";
import { UIContext } from "../contexts/UIContext";
import { CONSTANT } from "../utils/constants";
import { useAsyncGet, useAsyncGetAddress, useAsyncPOST, usePollGet } from "../utils/fetchUtils";
import { DeviceGetCommandResponse, DevicePushCommandResponse } from "../utils/response";
import "../styles/FunctionBox.css";

interface DeviceFunctionState {
  alarm: boolean;
  locate: boolean;
  wipe: boolean;
  lock: boolean;
}

const FunctionBox = () => {
  const [functionExecuting, setFunctionExecuting] = useState<DeviceFunctionState>({ alarm: false, locate: false, wipe: false, lock: false });
  const [functionMessage, setFunctionMessage] = useState("");
  const [functionMessageClass, setFunctionMessageClass] = useState("");
  const { device } = useContext(DeviceContext);
  const { locationDispatch } = useContext(LocationContext);
  const { uiState } = useContext(UIContext);
  const { formatMessage } = useIntl();
  const activeDeviceList = device.filter(dev => dev.isActive === true );
  const activeDevice = activeDeviceList[0];
  const shouldDisableFunction = activeDevice?.remainDays < 0;
  const disableButtonClass = shouldDisableFunction ? "functionbox-disabled" : "";
  const deviceCommandRequestBody = { UniqueID: activeDevice.uniqueID, PID: activeDevice.pid };
  const commandStatusQuery = { UniqueID: activeDevice.uniqueID };

  useEffect(() => {
    setFunctionMessage("");
    setFunctionMessageClass("");
  }, [activeDevice]);

  const updateLocation = (uniqueID: string) => {
    useAsyncGet(`${CONSTANT.URL.API}/${CONSTANT.API.DEVICE_LOCATION}`, { UniqueID: uniqueID }).then((data: { location: LocationData } ) => {
      if(data instanceof Error) {
        const payload = { uid: uniqueID, location: {} };
        locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
      } else {
        const { location } = (data as { location: LocationData });
        if(location.latitude && location.longitude) {
          useAsyncGetAddress(location.latitude, location.longitude).then( address => {
            if( address instanceof Error) {
              const payload = { uid: uniqueID, location: { ...location, address: "" } };
              locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
            } else {
              const payload = { uid: uniqueID, location: { ...location, address } };
              locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
            }
          });
        } else {
          const payload = { uid: uniqueID, location };
          locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
          setFunctionMessageClass("functionbox-errormessage");
          setFunctionMessage(formatMessage({ id: "error_locate_CS004" }));
        }
      }
    });
  };

  const onFunctionClick = (command: string, functionName: "alarm" | "locate" | "lock" | "wipe" ) => {
    setFunctionMessage("");
    setFunctionMessageClass("");
    const shouldNotRun = shouldDisableFunction || functionExecuting[functionName as keyof DeviceFunctionState];
    shouldNotRun ? null : (async () => {
      setFunctionExecuting({ ...functionExecuting, [functionName]: true });
      const pushCommandResp = await useAsyncPOST(`${CONSTANT.URL.API}/${CONSTANT.API.DEVICE_COMMAND}`, {...deviceCommandRequestBody, command });
      if(pushCommandResp instanceof Error || !((pushCommandResp as DevicePushCommandResponse) && (pushCommandResp as DevicePushCommandResponse).PushCommandResponse)) {
        setFunctionMessage(formatMessage({ id: `error_${functionName}` }).replace("{0}", activeDevice.displayName));
        setFunctionExecuting({...functionExecuting, [functionName]: false});
      } else {
        const { ReturnCode, Response } = (pushCommandResp as DevicePushCommandResponse).PushCommandResponse;
        if(ReturnCode === "200") {
          const { DEVICE_COMMAND_STATUS } = CONSTANT;
          const stopPoll = (resp: DeviceGetCommandResponse) => {
            const { CommandStatus } = resp.GetCommandResponse.Response;
            const shouldStopPoll = (CommandStatus === DEVICE_COMMAND_STATUS.CLIENT_EXECUTED || CommandStatus === DEVICE_COMMAND_STATUS.REACH_RETRY_LIMIT || CommandStatus === DEVICE_COMMAND_STATUS.FAILED);
            return shouldStopPoll;
          };
          const result = await usePollGet(`${CONSTANT.URL.API}/${CONSTANT.API.DEVICE_COMMAND_STATUS}`, { ...commandStatusQuery, TransactionID: Response.TransactionID }, stopPoll);
          console.log(result);
          const finalResult = result as DeviceGetCommandResponse;
          const { CommandStatus } = finalResult.GetCommandResponse.Response;

          if(CommandStatus === DEVICE_COMMAND_STATUS.CLIENT_EXECUTED) {
            setFunctionMessageClass("functionbox-successmessage");
            setFunctionMessage(formatMessage({ id: `message_${functionName}_success` }).replace("{0}", activeDevice.displayName));
            setTimeout(() => {
              setFunctionMessageClass("");
              setFunctionMessage("");
            }, 3000);
            functionName === "locate" ? updateLocation(activeDevice.uniqueID) : null;
          } else {
            setFunctionMessageClass("functionbox-errormessage");
            setFunctionMessage(formatMessage({ id: `error_${functionName}_${CommandStatus}` }));
          }
        } else {
          setFunctionMessageClass("functionbox-errormessage");
          setFunctionMessage(formatMessage({ id: "error_default" }));
        }
        setFunctionExecuting({...functionExecuting, [functionName]: false});
      }
    })();
  };

  const functionList = [{
    buttonClass: "functionbox-btn-alarm",
    wordingId: "label_alarm",
    androidOnly: false,
    triggered: functionExecuting.alarm,
    onClick: () => onFunctionClick(CONSTANT.DEVICE_COMMAND.ALARM, 'alarm'),
  },{
    buttonClass: "functionbox-btn-locate",
    wordingId: "label_locate",
    androidOnly: false,
    triggered: functionExecuting.locate,
    onClick: () => onFunctionClick(CONSTANT.DEVICE_COMMAND.LOCATE, 'locate'),
  },{
    buttonClass: "functionbox-btn-wipe",
    wordingId: "label_wipe",
    androidOnly: true,
    triggered: functionExecuting.wipe,
    onClick: () => onFunctionClick(CONSTANT.DEVICE_COMMAND.WIPE, 'wipe'),
  },{
    buttonClass: "functionbox-btn-lock",
    wordingId: "label_lock",
    androidOnly: true,
    triggered: functionExecuting.lock,
    onClick: () => onFunctionClick(CONSTANT.DEVICE_COMMAND.LOCK, 'lock'),
  },];

  const renderFunctions = () => {
    const filteredFunctionList = activeDevice.os === "Android" ? functionList : functionList.filter(f => f.androidOnly === false);
    return filteredFunctionList.map(f => (
      <div key={ f.wordingId } className={ `functionbox-menu-btn ${f.buttonClass} ${disableButtonClass}` } onClick={ f.onClick }>
        <div className={ `functionbox-icon ${disableButtonClass} ${f.triggered ? "functionbox-loading" : ""}` }></div>
        <div>
          <FormattedMessage id={ f.wordingId } />
        </div>
      </div>
    ));
  };

  return activeDevice && uiState.showDeviceFunction ? (
    <div className="functionbox">
      <div className="functionbox-functions">
        { renderFunctions() }
      </div>
      <div className={ `functionbox-message ${functionMessageClass}` }>{ functionMessage }</div>
    </div>
  ) : null;
};

export default FunctionBox;
